/**
 * EventWebsiteBuilderPage — organiser-facing builder for the event microsite.
 *
 * Structure: left pane = form sections (Hero, About, Agenda, Speakers, FAQs, Sponsors, Theme).
 *            right pane = quick preview link + section toggles.
 * Persists via PUT /api/events/{id}/microsite, with publish/unpublish toggles.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Save,
  Globe,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  HelpCircle,
  Users,
  Sparkles,
  Palette,
} from "lucide-react";

const TABS = [
  { key: "hero", label: "Hero" },
  { key: "about", label: "About" },
  { key: "agenda", label: "Agenda" },
  { key: "speakers", label: "Speakers" },
  { key: "faqs", label: "FAQs" },
  { key: "sponsors", label: "Sponsors" },
  { key: "theme", label: "Theme" },
];

const defaultMicrosite = {
  hero_eyebrow: "",
  hero_subtitle: "",
  hero_image: "",
  theme_color: "#0F4C81",
  accent_color: "#D4AF37",
  about_html: "",
  agenda: [],
  speakers: [],
  faqs: [],
  sponsor_ids: [],
  cta_label: "Register now",
  cta_link_override: "",
  sections_order: ["hero", "about", "agenda", "speakers", "councils", "sponsors", "faqs", "cta"],
  published: false,
};

export default function EventWebsiteBuilderPage() {
  const { eventId } = useParams();
  const [tab, setTab] = useState("hero");
  const [event, setEvent] = useState(null);
  const [site, setSite] = useState(defaultMicrosite);
  const [sponsors, setSponsors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [{ data: ms }, { data: ev }, { data: sp }] = await Promise.all([
          api.get(`/events/${eventId}/microsite`),
          api.get(`/events/${eventId}`),
          api.get(`/sponsors`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setEvent(ev);
        setSite({ ...defaultMicrosite, ...(ms.microsite || {}) });
        setSponsors(Array.isArray(sp) ? sp : sp || []);
      } catch (e) {
        toast.error("Could not load microsite settings.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/microsite`, site);
      toast.success("Microsite saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
    setSaving(false);
  };

  const togglePublish = async () => {
    setSaving(true);
    try {
      const path = site.published ? "unpublish" : "publish";
      await api.post(`/events/${eventId}/microsite/${path}`);
      setSite((s) => ({ ...s, published: !s.published }));
      toast.success(site.published ? "Microsite unpublished" : "Microsite published — public!");
    } catch {
      toast.error("Could not toggle publish.");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto py-20 text-center text-slate-500">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          <p className="mt-3 text-sm">Loading builder…</p>
        </div>
      </DashboardLayout>
    );
  }

  const previewHref = event?.slug ? `/events/${event.slug}` : null;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="event-builder">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Event Website Builder
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              {event?.title || "Untitled event"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Customise the public event page at <span className="font-mono">/events/{event?.slug}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {previewHref && (
              <a href={previewHref} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81]" data-testid="builder-preview-button">
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
              </a>
            )}
            <Button
              variant="outline"
              onClick={save}
              disabled={saving}
              className="border-slate-300"
              data-testid="builder-save-button"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save draft
            </Button>
            <Button
              onClick={togglePublish}
              disabled={saving}
              className={
                site.published
                  ? "bg-slate-200 text-slate-700 hover:bg-slate-300 font-bold uppercase tracking-[0.18em]"
                  : "bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
              }
              data-testid="builder-publish-button"
            >
              {site.published ? (
                <><EyeOff className="h-3.5 w-3.5 mr-1.5" /> Unpublish</>
              ) : (
                <><Globe className="h-3.5 w-3.5 mr-1.5" /> Publish</>
              )}
            </Button>
          </div>
        </div>

        {/* Status pill */}
        <div className="mt-4">
          <span
            className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full ${
              site.published
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-600"
            }`}
            data-testid="builder-status-pill"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${site.published ? "bg-emerald-500" : "bg-slate-400"}`} />
            {site.published ? "Published" : "Draft"}
          </span>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200 flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#0F4C81] text-[#0F4C81]"
                  : "border-transparent text-slate-500 hover:text-[#0A192F]"
              }`}
              data-testid={`builder-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 rounded-2xl bg-white border border-slate-200 p-6">
            {tab === "hero" && <HeroPane site={site} setSite={setSite} />}
            {tab === "about" && <AboutPane site={site} setSite={setSite} />}
            {tab === "agenda" && <AgendaPane site={site} setSite={setSite} />}
            {tab === "speakers" && <SpeakersPane site={site} setSite={setSite} />}
            {tab === "faqs" && <FaqsPane site={site} setSite={setSite} />}
            {tab === "sponsors" && <SponsorsPane site={site} setSite={setSite} sponsors={sponsors} />}
            {tab === "theme" && <ThemePane site={site} setSite={setSite} />}
          </div>

          {/* Right rail — preview tip + tips */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-2xl bg-[#0A192F] text-white p-6 border border-[#D4AF37]/30">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Live preview
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Save your draft and click <span className="font-semibold text-white">Preview</span> to see the published event page in a new tab.
                Hit <span className="font-semibold text-white">Publish</span> to make it live for delegates and sponsors.
              </p>
              {previewHref && (
                <a href={previewHref} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center text-[#D4AF37] text-xs font-bold uppercase tracking-[0.22em] hover:underline">
                  Open public page
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </a>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5 text-sm">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                What's included automatically
              </p>
              <ul className="mt-3 space-y-2 text-slate-600 text-xs">
                <li>• Event title + date + location pulled from event record</li>
                <li>• Council list pulled from your committees</li>
                <li>• Registration & payment flow wired to /events/{event?.slug}</li>
                <li>• QR codes on every issued certificate</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================================
// PANES
// ============================================================================
function HeroPane({ site, setSite }) {
  return (
    <div className="space-y-5" data-testid="builder-pane-hero">
      <PaneHeader icon={Sparkles} title="Hero section" subtitle="The first thing every visitor reads." />
      <Field label="Eyebrow line">
        <Input
          value={site.hero_eyebrow || ""}
          onChange={(e) => setSite((s) => ({ ...s, hero_eyebrow: e.target.value }))}
          placeholder="e.g. Flagship Conference 2026"
          data-testid="builder-hero-eyebrow"
        />
      </Field>
      <Field label="Hero subtitle">
        <Textarea
          value={site.hero_subtitle || ""}
          onChange={(e) => setSite((s) => ({ ...s, hero_subtitle: e.target.value }))}
          placeholder="One sentence that sells the event."
          rows={3}
          data-testid="builder-hero-subtitle"
        />
      </Field>
      <Field label="Hero image URL" help="Banner shown behind the hero. Defaults to the event's banner_url.">
        <Input
          value={site.hero_image || ""}
          onChange={(e) => setSite((s) => ({ ...s, hero_image: e.target.value }))}
          placeholder="https://images.unsplash.com/…"
          data-testid="builder-hero-image"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CTA label">
          <Input
            value={site.cta_label || ""}
            onChange={(e) => setSite((s) => ({ ...s, cta_label: e.target.value }))}
            placeholder="Register now"
            data-testid="builder-cta-label"
          />
        </Field>
        <Field label="CTA link override (optional)" help="Leave empty to use the built-in registration flow.">
          <Input
            value={site.cta_link_override || ""}
            onChange={(e) => setSite((s) => ({ ...s, cta_link_override: e.target.value }))}
            placeholder="https://…"
            data-testid="builder-cta-link"
          />
        </Field>
      </div>
    </div>
  );
}

function AboutPane({ site, setSite }) {
  return (
    <div className="space-y-5" data-testid="builder-pane-about">
      <PaneHeader icon={ImageIcon} title="About" subtitle="Tell delegates what makes this conference different." />
      <Field label="About text">
        <Textarea
          rows={10}
          value={site.about_html || ""}
          onChange={(e) => setSite((s) => ({ ...s, about_html: e.target.value }))}
          placeholder="Plain text — line breaks preserved. Use blank lines between paragraphs."
          data-testid="builder-about"
        />
      </Field>
    </div>
  );
}

function AgendaPane({ site, setSite }) {
  const add = () =>
    setSite((s) => ({
      ...s,
      agenda: [...(s.agenda || []), { day: "", time: "", title: "", description: "" }],
    }));
  const update = (i, key, val) =>
    setSite((s) => ({
      ...s,
      agenda: s.agenda.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)),
    }));
  const remove = (i) =>
    setSite((s) => ({ ...s, agenda: s.agenda.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5" data-testid="builder-pane-agenda">
      <div className="flex items-start justify-between gap-4">
        <PaneHeader icon={CalendarIcon} title="Agenda" subtitle="Day-wise breakdown shown in the public page." />
        <Button onClick={add} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="builder-agenda-add">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add row
        </Button>
      </div>
      <div className="space-y-3">
        {(site.agenda || []).map((row, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 grid sm:grid-cols-12 gap-3" data-testid={`builder-agenda-row-${i}`}>
            <Input className="sm:col-span-3" placeholder="Day 1 — 22 May" value={row.day || ""} onChange={(e) => update(i, "day", e.target.value)} />
            <Input className="sm:col-span-2" placeholder="09:00" value={row.time || ""} onChange={(e) => update(i, "time", e.target.value)} />
            <Input className="sm:col-span-3" placeholder="Title" value={row.title || ""} onChange={(e) => update(i, "title", e.target.value)} />
            <Input className="sm:col-span-3" placeholder="Description (optional)" value={row.description || ""} onChange={(e) => update(i, "description", e.target.value)} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              className="text-red-500 hover:bg-red-50 sm:col-span-1"
              data-testid={`builder-agenda-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {(site.agenda || []).length === 0 && (
          <p className="text-sm text-slate-500 italic">No agenda rows yet — add the opening ceremony first.</p>
        )}
      </div>
    </div>
  );
}

function SpeakersPane({ site, setSite }) {
  const add = () =>
    setSite((s) => ({ ...s, speakers: [...(s.speakers || []), { name: "", role: "", image: "", bio: "" }] }));
  const update = (i, key, val) =>
    setSite((s) => ({
      ...s,
      speakers: s.speakers.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)),
    }));
  const remove = (i) =>
    setSite((s) => ({ ...s, speakers: s.speakers.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5" data-testid="builder-pane-speakers">
      <div className="flex items-start justify-between gap-4">
        <PaneHeader icon={Users} title="Speakers & guests" subtitle="Headliners, judges and keynotes." />
        <Button onClick={add} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="builder-speakers-add">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add speaker
        </Button>
      </div>
      <div className="space-y-3">
        {(site.speakers || []).map((row, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 grid sm:grid-cols-12 gap-3" data-testid={`builder-speaker-row-${i}`}>
            <Input className="sm:col-span-3" placeholder="Full name" value={row.name || ""} onChange={(e) => update(i, "name", e.target.value)} />
            <Input className="sm:col-span-3" placeholder="Role / title" value={row.role || ""} onChange={(e) => update(i, "role", e.target.value)} />
            <Input className="sm:col-span-2" placeholder="Image URL" value={row.image || ""} onChange={(e) => update(i, "image", e.target.value)} />
            <Input className="sm:col-span-3" placeholder="Short bio" value={row.bio || ""} onChange={(e) => update(i, "bio", e.target.value)} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              className="text-red-500 hover:bg-red-50 sm:col-span-1"
              data-testid={`builder-speakers-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {(site.speakers || []).length === 0 && (
          <p className="text-sm text-slate-500 italic">No speakers added yet.</p>
        )}
      </div>
    </div>
  );
}

function FaqsPane({ site, setSite }) {
  const add = () =>
    setSite((s) => ({ ...s, faqs: [...(s.faqs || []), { q: "", a: "" }] }));
  const update = (i, key, val) =>
    setSite((s) => ({
      ...s,
      faqs: s.faqs.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)),
    }));
  const remove = (i) =>
    setSite((s) => ({ ...s, faqs: s.faqs.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5" data-testid="builder-pane-faqs">
      <div className="flex items-start justify-between gap-4">
        <PaneHeader icon={HelpCircle} title="FAQs" subtitle="Anticipate the top 5 delegate questions." />
        <Button onClick={add} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="builder-faqs-add">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add FAQ
        </Button>
      </div>
      <div className="space-y-3">
        {(site.faqs || []).map((row, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`builder-faq-row-${i}`}>
            <Input
              placeholder="Question"
              value={row.q || ""}
              onChange={(e) => update(i, "q", e.target.value)}
              data-testid={`builder-faq-q-${i}`}
            />
            <Textarea
              placeholder="Answer"
              rows={3}
              value={row.a || ""}
              onChange={(e) => update(i, "a", e.target.value)}
              data-testid={`builder-faq-a-${i}`}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              className="text-red-500 hover:bg-red-50"
              data-testid={`builder-faqs-remove-${i}`}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          </div>
        ))}
        {(site.faqs || []).length === 0 && (
          <p className="text-sm text-slate-500 italic">No FAQs yet.</p>
        )}
      </div>
    </div>
  );
}

function SponsorsPane({ site, setSite, sponsors }) {
  const toggle = (id) =>
    setSite((s) => {
      const cur = new Set(s.sponsor_ids || []);
      if (cur.has(id)) cur.delete(id);
      else cur.add(id);
      return { ...s, sponsor_ids: [...cur] };
    });

  return (
    <div className="space-y-5" data-testid="builder-pane-sponsors">
      <PaneHeader icon={Sparkles} title="Sponsors" subtitle="Pick which sponsors appear on the public microsite." />
      <div className="grid sm:grid-cols-2 gap-3">
        {(sponsors || []).map((s) => {
          const on = (site.sponsor_ids || []).includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.id)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                on
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-slate-200 hover:border-[#0F4C81]"
              }`}
              data-testid={`builder-sponsor-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[#0A192F]">{s.name}</p>
                <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full ${on ? "bg-[#D4AF37] text-[#0A192F]" : "bg-slate-100 text-slate-500"}`}>
                  {on ? "Featured" : "Hidden"}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{s.tier} · {s.tagline || "Sponsor"}</p>
            </button>
          );
        })}
        {sponsors.length === 0 && (
          <p className="text-sm text-slate-500 italic col-span-full">
            No sponsors created yet — add sponsors from the Super Admin / Org Admin sponsors page.
          </p>
        )}
      </div>
    </div>
  );
}

function ThemePane({ site, setSite }) {
  return (
    <div className="space-y-5" data-testid="builder-pane-theme">
      <PaneHeader icon={Palette} title="Theme" subtitle="Pick the colours used in your microsite hero." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Primary colour">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={site.theme_color || "#0F4C81"}
              onChange={(e) => setSite((s) => ({ ...s, theme_color: e.target.value }))}
              className="h-10 w-14 rounded border border-slate-300"
              data-testid="builder-theme-color"
            />
            <Input
              className="font-mono"
              value={site.theme_color || ""}
              onChange={(e) => setSite((s) => ({ ...s, theme_color: e.target.value }))}
            />
          </div>
        </Field>
        <Field label="Accent colour">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={site.accent_color || "#D4AF37"}
              onChange={(e) => setSite((s) => ({ ...s, accent_color: e.target.value }))}
              className="h-10 w-14 rounded border border-slate-300"
              data-testid="builder-accent-color"
            />
            <Input
              className="font-mono"
              value={site.accent_color || ""}
              onChange={(e) => setSite((s) => ({ ...s, accent_color: e.target.value }))}
            />
          </div>
        </Field>
      </div>
    </div>
  );
}

// ============================================================================
// helpers
// ============================================================================
function PaneHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-md bg-[#0F4C81]/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#0F4C81]" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-[#0A192F]">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {help && <p className="mt-1 text-[11px] text-slate-400">{help}</p>}
    </div>
  );
}
